import { Empty, formatUtc, useAsync } from '../../components/Async'
import { ApiError, trust } from '../../lib/api'

/**
 * How far this policy actually reached.
 *
 * The publish confirmation used to answer a different question. It printed the compiled size and then
 * a sentence about RSA-2048's 190-byte ceiling — a constant, correct for a key size no agent in this
 * fleet uses, and stating a device-specific quantity as a universal one. It told an operator a fact
 * about envelopes when they wanted a fact about their fleet.
 *
 * These four numbers answer it instead, and they are separate on purpose because they fail at
 * different steps. Targeted is an intent. Holding an envelope is a delivery fact — the device asked and
 * the server could seal something for it. Reporting it is a device fact. Reporting *this* revision
 * separates a rollout in progress from one that never started. "Published but nothing happened" is
 * four different faults, and one number cannot say which.
 *
 * `trust.publication` had been declared in the API client with no call site since Phase 6, which is
 * how two of its numbers stayed hardcoded zeros without anybody noticing (D-273).
 */
export function PublicationStatusCard({ policyId, reloadKey }: { policyId: string; reloadKey: number }) {
  const status = useAsync(async () => {
    try {
      return await trust.publication(policyId)
    } catch (error) {
      // Never published is a normal state for a draft, not an error worth a banner.
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }, [policyId, reloadKey])

  if (status.error) return null

  return (
    <div className="card" data-testid="publication-status">
      <div className="card-body">
        <h3 style={{ marginTop: 0 }}>Where this has reached</h3>

        {status.loading && !status.value ? <Empty>Loading…</Empty> : null}

        {!status.loading && status.value === null ? (
          <Empty>
            <span data-testid="publication-never-published">
              This policy has never been published, so no device has been offered it.
            </span>
          </Empty>
        ) : null}

        {status.value ? (
          <>
            {status.value.revokedUtc ? (
              <div className="banner warning" data-testid="publication-revoked">
                Revoked {formatUtc(status.value.revokedUtc)}
                {status.value.revokeReason ? `: ${status.value.revokeReason}` : '.'} Devices are told by
                a signed tombstone at their next check-in.
              </div>
            ) : null}

            <dl className="detail">
              <dt>Published</dt>
              <dd>
                revision {status.value.revision} · {formatUtc(status.value.publishedUtc)} ·{' '}
                {status.value.compiledBytes} bytes
              </dd>

              <dt>Targeted</dt>
              <dd data-testid="publication-devices-targeted">{status.value.devicesTargeted}</dd>

              <dt>Holding an envelope</dt>
              <dd data-testid="publication-devices-holding">{status.value.devicesHoldingEnvelope}</dd>

              <dt>Reporting it running</dt>
              <dd data-testid="publication-devices-reporting">
                {status.value.devicesReportingActive}
                {status.value.devicesReportingActive > status.value.devicesReportingThisRevision ? (
                  <span className="muted">
                    {' '}
                    ({status.value.devicesReportingThisRevision} on revision {status.value.revision})
                  </span>
                ) : null}
              </dd>
            </dl>

            {status.value.devicesTargeted > status.value.devicesHoldingEnvelope ? (
              <p className="hint" data-testid="publication-gap">
                {status.value.devicesTargeted - status.value.devicesHoldingEnvelope} targeted{' '}
                {status.value.devicesTargeted - status.value.devicesHoldingEnvelope === 1
                  ? 'device has'
                  : 'devices have'}{' '}
                not been given an envelope. Open a device to see why — a machine that has never checked
                in has never asked for one, and one this deployment cannot seal for says so.
              </p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  )
}
